// All site content lives here so it is easy to edit later.

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export const CRITICAL_PATH_NODES = [
  "Baseline",
  "Weekly Update",
  "Delay Flagged",
  "Recovery",
  "Handover",
];

export const CREDENTIALS = [
  "Primavera P6",
  "ASTA Powerproject",
  "MS Project",
  "NEC / Clause 32 Reporting",
  "MSc Construction & Project Management",
  "UK Infrastructure Experience",
];

export const GAPS = [
  {
    n: "01",
    title: "No one owns the programme",
    body: "The baseline is built once, then drifts. Site progress and the schedule quietly diverge until a deadline forces a reckoning.",
  },
  {
    n: "02",
    title: "Reporting is reactive",
    body: "Updates are pulled together the night before a meeting — backward-looking, inconsistent, and impossible to trust for decisions.",
  },
  {
    n: "03",
    title: "Weak footing in disputes",
    body: "When delay claims arrive, there is no contemporaneous record, no float analysis, no defensible narrative to stand behind.",
  },
];

export const HOME_SERVICES = [
  { tag: "01", title: "Baseline Programmes", body: "A structured, agreed sequence with a clean WBS and logic your whole team can hold the site to." },
  { tag: "02", title: "Progress Monitoring", body: "Regular, disciplined updates that keep the programme and the ground in step — every cycle." },
  { tag: "03", title: "Look-Ahead Scheduling", body: "Short-interval look-aheads that turn the master programme into what the team does this fortnight." },
  { tag: "04", title: "Delay & Variance Analysis", body: "Early flagging of slippage with float and critical-path analysis — before it becomes a claim." },
  { tag: "05", title: "Procurement & Production Planning", body: "Sequenced procurement and production schedules aligned to the construction programme." },
  { tag: "06", title: "Management Reporting", body: "Clear, board-ready reporting that gives leadership and clients real visibility of position." },
];

export const VALUES = [
  { n: "01", title: "Outcome-Led", body: "We are measured on visibility, early warning, and defensible records — not on the volume of documents produced." },
  { n: "02", title: "Specialist, Not Generalist", body: "We do one thing: planning and project controls. It is a discipline, not a side task bolted onto a PM role." },
  { n: "03", title: "Built On Evidence", body: "Every programme, update, and report is contemporaneous and traceable — a record that holds up under scrutiny." },
];

export const EXPERIENCE = [
  { title: "Civil Engineering & Groundworks", body: "Earthworks, structures and enabling works where sequencing and float discipline decide the outcome." },
  { title: "Infrastructure & Public Works", body: "Utilities, drainage and public-realm programmes with multiple interfaces and stakeholders." },
  { title: "Defence & Institutional Projects", body: "Secure, controlled environments demanding rigorous change control and reporting." },
  { title: "Transport & Highways", body: "Highways and transport schemes with phased access, possessions and tight constraints." },
  { title: "Precast & Production Planning", body: "Factory and precast production schedules synchronised with site demand." },
  { title: "NEC & Formal Contract Reporting", body: "Programme submissions and reporting aligned to NEC / Clause 32 obligations." },
];

export const PROCESS = [
  { n: "01", title: "Free health-check", body: "We review your current programme, or its absence, and show you exactly where the risk sits." },
  { n: "02", title: "Scope agreed", body: "We define the controls cycle, reporting cadence and outputs that fit your project — and only those." },
  { n: "03", title: "Baseline built or audited", body: "We build a clean baseline from scratch, or audit and repair the one you already have." },
  { n: "04", title: "Monthly cycle begins", body: "Updates, look-aheads, delay analysis and reporting run on a steady, dependable rhythm." },
];

export const SERVICES = [
  { tag: "Programme", title: "Baseline Programme Development", body: "A defensible baseline built on a proper WBS and sound logic — the single source of truth for the project.", points: ["Work breakdown structure & coding", "Logic-linked activity network", "Resource & cost loading where required", "Client / NEC-ready submission format"] },
  { tag: "Control", title: "Baseline & Change Management", body: "Keeping the baseline authoritative as scope and instructions change, with a clean audit trail.", points: ["Change register & revision control", "Impact assessment on completion", "Re-baselining when justified", "Full traceability of every revision"] },
  { tag: "Monitoring", title: "Progress Monitoring & Updates", body: "Disciplined periodic updates that keep the programme honest against actual site progress.", points: ["Progress capture & percent complete", "Actual vs planned tracking", "Data date discipline", "Variance highlighting each cycle"] },
  { tag: "Analysis", title: "Critical Path & Float Analysis", body: "Understanding what truly drives completion — and how much protection remains in the programme.", points: ["Longest-path identification", "Total & free float analysis", "Driving-logic interrogation", "Constraint & calendar review"] },
  { tag: "Planning", title: "Look-Ahead Scheduling", body: "Short-interval look-aheads that translate the master programme into actionable site targets.", points: ["2 / 4 / 6-week look-aheads", "Constraint & readiness checks", "Aligned to the master programme", "Site-team friendly formats"] },
  { tag: "Forensic", title: "Delay & Time-Slice Analysis", body: "Structured analysis of what happened, when, and why — evidence you can rely on in negotiation.", points: ["Time-slice / windows analysis", "As-planned vs as-built", "Cause & effect narrative", "Contemporaneous record support"] },
  { tag: "Recovery", title: "Recovery Programme Development", body: "Realistic, logic-driven recovery options when a project falls behind — not wishful acceleration.", points: ["Options & scenario modelling", "Acceleration & re-sequencing", "Resource implication review", "Achievability testing"] },
  { tag: "Supply", title: "Procurement & Production Planning", body: "Sequencing procurement and production so materials and elements arrive when the programme needs them.", points: ["Procurement schedules", "Lead-time integration", "Production / precast planning", "Interface with construction logic"] },
  { tag: "Reporting", title: "Programme Reporting & Dashboards", body: "Clear, consistent reporting that gives leadership and clients a trustworthy read on position.", points: ["Executive & board reporting", "KPI & milestone dashboards", "Trend & forecast views", "Consistent monthly cadence"] },
];

export const VALUE_EXCHANGE = [
  { service: "Baseline programme", benefit: "A clear, agreed sequence your leadership and clients can hold the site team to." },
  { service: "Progress monitoring", benefit: "You always know the real position — not a guess, and not last month's number." },
  { service: "Look-ahead scheduling", benefit: "The site team knows precisely what to hit over the next few weeks." },
  { service: "Delay & variance analysis", benefit: "Slippage is caught early, while there is still time to act — and priced fairly." },
  { service: "Critical path analysis", benefit: "You focus resource on the activities that actually move completion." },
  { service: "Programme reporting", benefit: "Board meetings and client reviews run on facts, not firefighting." },
];

export const PACKAGES = [
  {
    name: "Progress Watch",
    tagline: "For contractors who have a programme but no one to keep it honest.",
    features: ["Periodic progress updates", "Actual vs planned tracking", "Monthly management report", "Early slippage flagging"],
    recommended: false,
  },
  {
    name: "Full Project Controls",
    tagline: "End-to-end planning and controls for a single live project.",
    features: ["Baseline build or audit", "Full update & look-ahead cycle", "Critical path & float analysis", "Delay analysis & recovery support", "Executive reporting & dashboards"],
    recommended: true,
  },
  {
    name: "Multi-Project Controls",
    tagline: "A portfolio view for contractors running several jobs at once.",
    features: ["Controls across multiple projects", "Portfolio-level reporting", "Cross-project resource visibility", "Standardised programme governance", "Priority response"],
    recommended: false,
  },
];

export const CULTURE = [
  { stat: "Day one", label: "Live projects from the start" },
  { stat: "Direct", label: "Client exposure, not layers" },
  { stat: "Real", label: "Ownership of the programme" },
  { stat: "Growing", label: "An early, ambitious team" },
];

export const REASONS = [
  { value: "health-check", label: "Programme health-check" },
  { value: "services", label: "Planning & project controls services" },
  { value: "careers", label: "Careers" },
  { value: "other", label: "Something else" },
];
